// ===== Saúde do pipeline: critério de saída, envelhecimento e forecast ajustado =====
// Três regras que ficam ACIMA das etapas, sem mudar o funil:
// 1) cada etapa tem critério de saída verificável + um campo que o CRM cobra;
// 2) "parado" é medido contra a mediana histórica da etapa naquele funil (1,8×),
//    e inatividade (sem atividade registrada) é um sinal separado;
// 3) probabilidade por etapa vem da conversão histórica e cai com a idade.

import React from 'react';
import { Icon } from './icons.jsx';
import { TODAY } from './data.jsx';

// ---- 1. Critério de saída e campo obrigatório por etapa ----
// O gate é avaliado ao ENTRAR na etapa: "para entrar aqui, isto precisa ser verdade".
const STAGE_GATES = {
  QUALIFICADO: {
    criterio: "Necessidade, verba e prazo confirmados pelo cliente",
    exige: "BANT com necessidade, verba e prazo preenchidos",
    onde: "aba BANT do negócio",
    check: (l) => { const b = l.bant || {}; return ["need", "budget", "timeline"].every(k => String(b[k] || "").trim().length > 1); },
  },
  REUNIAO: {
    criterio: "Reunião realizada com quem decide",
    // Antes o gate conferia só contato e cargo, e o toggle "É o decisor da
    // compra" não entrava na conta: preencher o cargo abria a trava e marcar o
    // decisor não fazia nada. Quem decide é afirmação de alguém, não dedução a
    // partir de um cargo preenchido.
    exige: "Contato, cargo e confirmação de quem decide",
    onde: "aba Dados do negócio",
    check: (l) => !!String(l.contato || "").trim() && !!String(l.cargo || "").trim()
                  && l.cargo !== "—" && !!l.contatoDecisor,
  },
  PROPOSTA: {
    criterio: "Escopo aceito e proposta entregue ao cliente",
    exige: "Ao menos uma proposta criada no negócio",
    onde: "aba Propostas do negócio",
    check: (l, ctx) => (ctx.propostas || []).length > 0,
  },
  ORCAMENTO: {
    criterio: "Orçamento enviado ao lojista",
    exige: "Ao menos uma proposta criada no negócio",
    onde: "aba Propostas do negócio",
    check: (l, ctx) => (ctx.propostas || []).length > 0,
  },
  NEGOCIACAO: {
    criterio: "Cliente negocia preço ou prazo da proposta entregue",
    exige: "Valor do negócio definido (> 0)",
    onde: "cabeçalho do negócio",
    check: (l, ctx) => (l.valor || 0) > 0 || (ctx.propostas || []).some(p => (p.total || 0) > 0),
  },
  GANHO: {
    criterio: "Pedido confirmado pelo cliente",
    exige: "Proposta selecionada para fechamento",
    onde: "aba Propostas do negócio",
    check: (l, ctx) => (ctx.propostas || []).some(p => p.selecionadaFechamento),
  },
};
function gateStatus(lead, stageId, ctx = {}) {
  const gate = STAGE_GATES[stageId] || null;
  if (!gate || !lead) return { ok: true, gate: null };
  let ok = false;
  try { ok = !!gate.check(lead, ctx); } catch { ok = false; }
  return { ok, gate };
}

// ---- 2. Envelhecimento ----
const PH_MIN_AMOSTRA = 5;        // abaixo disso a mediana não é confiável → cai no SLA
const PH_FATOR_PARADO = 1.8;     // limite de "parado" = 1,8 × mediana da etapa
const PH_DIAS_INATIVO = 14;      // sem atividade registrada em etapa média/final
const PH_PROB_PADRAO = { ENTRADA: 3, OUT_NOVA: 5, QUALIFICADO: 20, REUNIAO: 35, PROPOSTA: 55, NEGOCIACAO: 75, ORCAMENTO: 45, GANHO: 100 };

const phDias = (iso) => iso ? Math.max(0, Math.floor((TODAY.getTime() - new Date(iso).getTime()) / 86400000)) : null;
function phDiasParado(lead) {
  const d = phDias(lead.ultimoMovimento);
  return d == null ? (lead.diasNoEstagio || 0) : d;
}
function phDiasSemAtividade(lead) {
  return phDias(lead.ultimaAtividade || lead.ultimoMovimento);
}
// Mediana de dias na etapa, colhida de historicoEtapas (gravado a cada movimento).
function medianaEtapa(leads, funil, stageId) {
  const am = [];
  for (const l of leads) {
    if (funil && l.funil !== funil) continue;
    for (const h of (l.historicoEtapas || [])) if (h.stage === stageId && h.dias >= 0) am.push(h.dias);
  }
  if (am.length < PH_MIN_AMOSTRA) return null;
  am.sort((a, b) => a - b);
  const m = Math.floor(am.length / 2);
  return { mediana: am.length % 2 ? am[m] : Math.round((am[m - 1] + am[m]) / 2), amostra: am.length };
}
function limiteParado(leads, funil, stageId, stages) {
  const med = medianaEtapa(leads, funil, stageId);
  if (med) return { limite: Math.max(2, Math.round(med.mediana * PH_FATOR_PARADO)), base: "mediana", ...med };
  const sla = (stages || []).find(s => s.id === stageId)?.slaEmDias;
  return { limite: sla || null, base: sla ? "sla" : "sem-base", mediana: null, amostra: 0 };
}
function penalidadeIdade(dias, limite, semAtividade) {
  let f = 1;
  if (limite) { if (dias >= limite * 2) f = 0.5; else if (dias >= limite) f = 0.75; }
  if (semAtividade != null && semAtividade >= 21) f = Math.min(f, 0.6);
  return f;
}
// Diagnóstico completo de um negócio (usado no card e na Gestão).
function leadHealth(lead, leads, stages) {
  const dias = phDiasParado(lead);
  const { limite, base, mediana, amostra } = limiteParado(leads, lead.funil, lead.stage, stages);
  const semAtiv = phDiasSemAtividade(lead);
  // No fallback por SLA (sem amostra de mediana), o limite é comparado contra
  // dias desde o último movimento — não contra `dias`, que sem
  // ultimo_movimento_em cai para dias_no_estagio (tempo na etapa, não tempo
  // parado) e acusaria todo negócio nunca movimentado. Quando há mediana, a
  // amostra já vem de dias_no_estagio de outros negócios, então comparar o
  // próprio `dias` (com o mesmo fallback) contra ela continua correto.
  const diasParaAlerta = base === "sla" ? phDias(lead.ultimoMovimento) : dias;
  const parado = limite && diasParaAlerta != null && diasParaAlerta >= limite
    ? (diasParaAlerta >= limite * 1.5 ? "danger" : "warn") : null;
  return {
    dias, limite, base, mediana, amostra, semAtiv, parado,
    inativo: semAtiv != null && semAtiv >= PH_DIAS_INATIVO,
    penalidade: penalidadeIdade(dias, limite, semAtiv),
  };
}
function phBaseLabel(h) {
  if (h.base === "mediana") return `mediana de ${h.mediana}d na etapa (${h.amostra} negócios)`;
  if (h.base === "sla") return `SLA da etapa (${h.limite}d) — histórico ainda insuficiente`;
  return "sem base de comparação";
}

// ---- 3. Probabilidade histórica por etapa ----
// Dos negócios que passaram pela etapa, quantos foram ganhos. Sem amostra → padrão.
function conversaoHistorica(leads, funil, stages) {
  const ordem = (stages || []).map(s => s.id);
  const out = {};
  for (const st of ordem) {
    let entraram = 0, ganhos = 0;
    for (const l of leads) {
      if (funil && l.funil !== funil) continue;
      const iAtual = ordem.indexOf(l.stage);
      const passou = (l.historicoEtapas || []).some(h => h.stage === st) || (iAtual >= 0 && iAtual >= ordem.indexOf(st));
      if (!passou) continue;
      entraram++;
      if (l.stage === "GANHO" && !l.perdido) ganhos++;
    }
    out[st] = entraram >= PH_MIN_AMOSTRA
      ? { pct: Math.round(ganhos / entraram * 100), amostra: entraram, base: "historico" }
      : { pct: PH_PROB_PADRAO[st] ?? 10, amostra: entraram, base: "padrao" };
  }
  return out;
}

// ---- Modal de critério não atendido (avançar exige justificativa) ----
function GateModal({ lead, gate, targetStageName, onCancel, onConfirm }) {
  const [just, setJust] = React.useState("");
  const ok = just.trim().length >= 5;
  return (
    <div className="modal-back" onClick={onCancel}>
      <div className="modal gate-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div style={{ width: 32, height: 32, background: "var(--color-warn-soft)", color: "var(--color-warn)", borderRadius: "var(--radius-sm)", display: "grid", placeItems: "center" }}>
            <Icon name="shield-check" size={16} />
          </div>
          <div className="title">Critério de saída não atendido</div>
          <button className="icon-btn" onClick={onCancel}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: "var(--color-ink-soft)", marginBottom: 12 }}>
            <strong style={{ color: "var(--color-ink)" }}>{lead?.empresa}</strong> está indo para <strong style={{ color: "var(--color-ink)" }}>{targetStageName}</strong>.
          </div>
          <div className="gate-box">
            <div className="gate-row"><span className="lbl">Critério</span><span className="val">{gate?.criterio}</span></div>
            <div className="gate-row falta"><span className="lbl">Falta</span><span className="val">{gate?.exige} <span className="onde">· {gate?.onde}</span></span></div>
          </div>
          <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label>Justificativa <span style={{ color: "var(--color-danger)" }}>*</span> <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--color-ink-faint)", marginLeft: 6 }}>· fica na timeline do negócio</span></label>
            <textarea className="textarea" placeholder="Ex: cliente confirmou verba por telefone, BANT será preenchido hoje." value={just} onChange={e => setJust(e.target.value)} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onCancel}>Voltar e preencher</button>
          <button className="btn primary" disabled={!ok} style={{ opacity: ok ? 1 : 0.5 }} onClick={() => onConfirm(just.trim())}>
            <Icon name="arrow-right" size={14} /> Avançar com justificativa
          </button>
        </div>
      </div>
    </div>
  );
}

export {
  STAGE_GATES, gateStatus, GateModal,
  medianaEtapa, limiteParado, penalidadeIdade, leadHealth, phBaseLabel,
  phDiasParado, phDiasSemAtividade, conversaoHistorica,
  PH_DIAS_INATIVO, PH_FATOR_PARADO, PH_MIN_AMOSTRA, PH_PROB_PADRAO,
};
